import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
    Timeline,
    Card,
    Input,
    Button,
    Spin,
    message,
    Row,
    Col,
    Typography,
    Select
} from "antd";
import {
    ClockCircleOutlined,
    CarOutlined,
    SearchOutlined
} from "@ant-design/icons";
import { Globe, ChevronDown, Menu, X, Sun, Moon, LogIn } from "lucide-react";
import axios from "axios";
import dayjs from "dayjs";
import LogoIndak from "../../../assets/img/logo-pdf.png";
import { getAutoTheme } from "../../../utils/themeHelper";
import "../../home/home.css";
import { BASE_API } from "../../../setup/application.properties";
import { 
    PROSES_ORDER_BARANG, 
    PROSES_ORDER_UANG_IN, 
    PROSES_ORDER_UANG_OUT, 
    statusConfigBarang, 
    statusConfigUangIn, 
    statusConfigUangOut 
} from "../../../utils/dataTracking";

const { Title, Text } = Typography;
const { Option } = Select;

export default function MainTracking() {
    const navigate = useNavigate();
    const { kodePengiriman, noResiParam } = useParams();
    const [noResi, setNoResi] = useState("");
    const [loading, setLoading] = useState(false);
    const [selectedPengiriman, setSelectedPengiriman] = useState(null);
    const [data, setData] = useState([]);
    
    // Language & Theme State
    const [lang, setLang] = useState(localStorage.getItem("lang") || "en");
    const [langOpen, setLangOpen] = useState(false);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [theme, setTheme] = useState(getAutoTheme);
    const isLoggedIn = localStorage.getItem("isLoggedIn") === "true";

    useEffect(() => {
        document.documentElement.dir = lang === "ar" ? "rtl" : "ltr";
        document.documentElement.lang = lang;
    }, [lang]);

    // Sync theme
    useEffect(() => {
        document.documentElement.setAttribute("data-theme", theme);
    }, [theme]);

    const toggleTheme = () => {
        const newTheme = theme === "light" ? "dark" : "light";
        setTheme(newTheme);
        localStorage.setItem("landing-theme", newTheme);
    };

    const changeLanguage = (newLang) => {
        setLang(newLang);
        localStorage.setItem("lang", newLang);
        setLangOpen(false);
    };

    const fetchTracking = async (kode = selectedPengiriman, resi = noResi) => {
        if (!resi) {
            message.warning(lang === "ar" ? "يرجى إدخال رقم التتبع" : "Please enter tracking number");
            return;
        }
        if (!kode) {
            message.warning(lang === "ar" ? "يرجى اختيار نوع الشحنة" : "Please select delivery type");
            return;
        }

        try {
            setLoading(true);
            const res = await axios.get(
                `${BASE_API}/api/tracking/${resi}?kodePengiriman=${kode}`
            );

            const prosesOrder =
                kode === "01"
                    ? PROSES_ORDER_BARANG
                    : kode === "02"
                    ? PROSES_ORDER_UANG_OUT
                    : kode === "03"
                    ? PROSES_ORDER_UANG_IN
                    : {};

            const sorted = [...res.data.data].sort(
                (a, b) => (prosesOrder[a.kodeProses] || 0) - (prosesOrder[b.kodeProses] || 0)
            );

            setData(sorted);
        } catch (error) {
            message.error(lang === "ar" ? "فشل استرجاع بيانات التتبع" : "Failed to load tracking data");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (kodePengiriman && noResiParam) {
            setSelectedPengiriman(kodePengiriman);
            setNoResi(noResiParam);
            fetchTracking(kodePengiriman, noResiParam);
        }
    }, [kodePengiriman, noResiParam]);

    const text = {
        en: {
            navHome: "Home",
            navAbout: "About",
            navServices: "Services",
            navContact: "Contact",
            navCheckRates: "Check Rates",
            navSignIn: "Sign In",
            navDashboard: "Dashboard",
            title: "🚚 Tracking Delivery",
            placeholderSelect: "Pilih Jenis Pengiriman",
            placeholderInput: "Masukkan Nomor Resi",
            btnSubmit: "Lacak Pengiriman",
            labelCargo: "Barang",
            labelRemitOut: "Uang ke Luar Negeri",
            labelRemitIn: "Uang ke Indonesia",
            footerDesc: "Trusted international logistics and remittance solutions from Indonesia to Yemen and Saudi Arabia.",
            footerCopy: "All rights reserved."
        },
        ar: {
            navHome: "الرئيسية",
            navAbout: "من نحن",
            navServices: "خدماتنا",
            navContact: "اتصل بنا",
            navCheckRates: "أسعار الشحن",
            navSignIn: "تسجيل الدخول",
            navDashboard: "لوحة التحكم",
            title: "🚚 تتبع الشحنات",
            placeholderSelect: "اختر نوع الشحنة",
            placeholderInput: "أدخل رقم التتبع",
            btnSubmit: "تتبع الآن",
            labelCargo: "بضائع",
            labelRemitOut: "أموال إلى الخارج",
            labelRemitIn: "أموال إلى إندونيسيا",
            footerDesc: "حلول لوجستية وحوالات مالية دولية موثوقة من إندونيسيا إلى اليمن والمملكة العربية السعودية.",
            footerCopy: "جميع الحقوق محفوظة."
        },
        id: {
            navHome: "Beranda",
            navAbout: "Tentang Kami",
            navServices: "Layanan",
            navContact: "Kontak",
            navCheckRates: "Cek Ongkir",
            navSignIn: "Masuk",
            navDashboard: "Dashboard",
            title: "🚚 Lacak Pengiriman",
            placeholderSelect: "Pilih Jenis Pengiriman",
            placeholderInput: "Masukkan Nomor Resi",
            btnSubmit: "Lacak Pengiriman",
            labelCargo: "Barang",
            labelRemitOut: "Uang ke Luar Negeri",
            labelRemitIn: "Uang ke Indonesia",
            footerDesc: "Solusi logistik dan transfer uang internasional terpercaya dari Indonesia ke Yaman dan Arab Saudi.",
            footerCopy: "Hak cipta dilindungi undang-undang."
        }
    };

    const t = text[lang] || text.en;

    const kodePengirimanList = [
        {
            kode: "01",
            label: t.labelCargo
        },
        {
            kode: "02",
            label: t.labelRemitOut
        },
        {
            kode: "03",
            label: t.labelRemitIn
        }
    ];

    return (
        <div className="landing-container" dir={lang === "ar" ? "rtl" : "ltr"}>
            {/* HEADER NAVBAR */}
            <header className="landing-header">
                <div className="header-container">
                    <div className="header-logo" onClick={() => navigate("/")}>
                        <img src={LogoIndak} alt="Indak Logo" className="logo-img" />
                    </div>

                    <nav className="desktop-nav">
                        <button onClick={() => navigate("/")} className="nav-link">{t.navHome}</button>
                        <button onClick={() => navigate("/about")} className="nav-link">{t.navAbout}</button>
                        <button onClick={() => navigate("/services")} className="nav-link">{t.navServices}</button>
                        <button onClick={() => navigate("/contact")} className="nav-link">{t.navContact}</button>
                        <button onClick={() => navigate("/check-rates")} className="nav-link">{t.navCheckRates}</button>
                        
                        {/* Language Switcher */}
                        <div className="lang-dropdown-container">
                            <button className="lang-dropdown-trigger" onClick={() => setLangOpen(!langOpen)}>
                                <Globe size={16} />
                                <span>{lang === "en" ? "EN" : lang === "id" ? "ID" : "AR"}</span>
                                <ChevronDown size={14} />
                            </button>
                            {langOpen && (
                                <div className="lang-dropdown-menu">
                                    <button className={`lang-dropdown-item ${lang === "en" ? "active-lang" : ""}`} onClick={() => changeLanguage("en")}>
                                        English
                                    </button>
                                    <button className={`lang-dropdown-item ${lang === "id" ? "active-lang" : ""}`} onClick={() => changeLanguage("id")}>
                                        Bahasa Indonesia
                                    </button>
                                    <button className={`lang-dropdown-item ${lang === "ar" ? "active-lang" : ""}`} onClick={() => changeLanguage("ar")}>
                                        العربية
                                    </button>
                                </div>
                            )}
                        </div>

                        {/* Theme Toggle Button */}
                        <button onClick={toggleTheme} className="theme-toggle-btn" title={theme === "light" ? "Switch to Dark Mode" : "Switch to Light Mode"}>
                            {theme === "light" ? <Moon size={18} /> : <Sun size={18} />}
                        </button>

                        {/* Login Door Icon Button */}
                        <button 
                            onClick={() => navigate(isLoggedIn ? "/admin/dashboard" : "/login")} 
                            className="nav-sig-button" 
                            title={isLoggedIn ? t.navDashboard : t.navSignIn}
                        >
                            <LogIn size={20} />
                        </button>
                    </nav>

                    <button className="mobile-menu-btn" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
                        {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
                    </button>
                </div>

                {mobileMenuOpen && (
                    <div className="mobile-nav">
                        <button onClick={() => navigate("/")} className="mobile-nav-link">{t.navHome}</button>
                        <button onClick={() => navigate("/about")} className="mobile-nav-link">{t.navAbout}</button>
                        <button onClick={() => navigate("/services")} className="mobile-nav-link">{t.navServices}</button>
                        <button onClick={() => navigate("/contact")} className="mobile-nav-link">{t.navContact}</button>
                        <button onClick={() => navigate("/check-rates")} className="mobile-nav-link">{t.navCheckRates}</button>
                        
                        <div style={{ display: "flex", gap: "20px", padding: "12px 0", alignItems: "center" }}>
                            <div style={{ display: "flex", gap: "8px" }}>
                                <button 
                                    onClick={() => changeLanguage("en")} 
                                    style={{ background: "none", border: "none", color: lang === "en" ? "#c82333" : "#aaa", fontWeight: "700", cursor: "pointer" }}
                                >
                                    EN
                                </button>
                                <span style={{ color: "#444" }}>|</span>
                                <button 
                                    onClick={() => changeLanguage("id")} 
                                    style={{ background: "none", border: "none", color: lang === "id" ? "#c82333" : "#aaa", fontWeight: "700", cursor: "pointer" }}
                                >
                                    ID
                                </button>
                                <span style={{ color: "#444" }}>|</span>
                                <button 
                                    onClick={() => changeLanguage("ar")} 
                                    style={{ background: "none", border: "none", color: lang === "ar" ? "#c82333" : "#aaa", fontWeight: "700", cursor: "pointer" }}
                                >
                                    AR
                                </button>
                            </div>

                            <button 
                                onClick={toggleTheme} 
                                style={{ background: "none", border: "none", color: "var(--header-link)", cursor: "pointer", padding: "4px" }}
                            >
                                {theme === "light" ? <Moon size={18} /> : <Sun size={18} />}
                            </button>
                        </div>

                        <button onClick={() => navigate(isLoggedIn ? "/admin/dashboard" : "/login")} className="mobile-nav-sig-cta">
                            <LogIn size={18} /> {isLoggedIn ? t.navDashboard : t.navSignIn}
                        </button>
                    </div>
                )}
            </header>

            {/* TRACKING CONTENT */}
            <div style={{ paddingTop: "140px", paddingBottom: "80px", minHeight: "80vh" }}>
                <Row justify="center" style={{ padding: "0 20px" }}>
                    <Col xs={24} sm={22} md={18} lg={14}>
                        <Card
                            bordered={false}
                            style={{
                                borderRadius: 12,
                                boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
                                background: "rgba(20, 20, 20, 0.75)",
                                border: "1px solid rgba(255, 255, 255, 0.08)"
                            }}
                        >
                            <Title level={3} style={{ textAlign: "center", color: "#fff", marginBottom: 30 }}>
                                {t.title}
                            </Title>
                            <Row gutter={[16, 16]} style={{ marginBottom: 20 }}>
                                <Col xs={24} sm={8}>
                                    <Select
                                        allowClear
                                        size="large"
                                        placeholder={t.placeholderSelect}
                                        value={selectedPengiriman}
                                        onChange={(value) => setSelectedPengiriman(value)}
                                        style={{ width: "100%" }}
                                        dropdownStyle={{ background: "#1a1a1a" }}
                                    >
                                        {kodePengirimanList.map((c) => (
                                            <Option key={c.kode} value={c.kode}>
                                                {c.label}
                                            </Option>
                                        ))}
                                    </Select>
                                </Col>
                                <Col xs={24} sm={16}>
                                    <Input
                                        size="large"
                                        placeholder={t.placeholderInput}
                                        prefix={<CarOutlined style={{ color: "#aaa" }} />}
                                        value={noResi}
                                        onChange={(e) => setNoResi(e.target.value)}
                                        onPressEnter={() => fetchTracking()}
                                        style={{ background: "rgba(255,255,255,0.05)", color: "#fff", border: "1px solid rgba(255,255,255,0.1)" }}
                                    />
                                </Col>
                            </Row>
                            <Button
                                type="primary"
                                size="large"
                                block
                                icon={<SearchOutlined />}
                                onClick={() => fetchTracking()}
                                disabled={!noResi || !selectedPengiriman}
                                style={{ background: "var(--primary)", borderColor: "var(--primary)" }}
                            >
                                {t.btnSubmit}
                            </Button>

                            <Spin spinning={loading}>
                                {data.length > 0 && (
                                    <Timeline
                                        style={{ marginTop: 32 }}
                                        mode="left"
                                    >
                                        {data.map((item, index) => {
                                            const cfg = statusConfigBarang[item.kodeProses] || {};
                                            const cfg2 = statusConfigUangOut[item.kodeProses] || {};
                                            const cfg3 = statusConfigUangIn[item.kodeProses] || {};
                                            const date = dayjs(item.createdAt);

                                            return (
                                                <Timeline.Item
                                                    key={index}
                                                    color={
                                                        selectedPengiriman === "01" ? cfg.color :
                                                        selectedPengiriman === "02" ? cfg2.color :
                                                        selectedPengiriman === "03" ? cfg3.color : null
                                                    }
                                                    dot={
                                                        selectedPengiriman === "01" ? <div className={`timeline-dot-wrapper ${cfg.color}`}>{cfg.icon}</div> :
                                                        selectedPengiriman === "02" ? <div className={`timeline-dot-wrapper ${cfg2.color}`}>{cfg2.icon}</div> :
                                                        selectedPengiriman === "03" ? <div className={`timeline-dot-wrapper ${cfg3.color}`}>{cfg3.icon}</div> : null
                                                    }
                                                >
                                                    <Card
                                                        size="small"
                                                        bordered={false}
                                                        style={{
                                                            background: "rgba(255, 255, 255, 0.03)",
                                                            border: "1px solid rgba(255, 255, 255, 0.05)",
                                                            borderRadius: 8,
                                                            animation: "fadeIn 0.6s ease"
                                                        }}
                                                    >
                                                        <Text strong style={{ fontSize: 16, color: "#fff" }}>
                                                            {
                                                                selectedPengiriman === "01" ? cfg.label :
                                                                selectedPengiriman === "02" ? cfg2.label :
                                                                selectedPengiriman === "03" ? cfg3.label : null
                                                                || item.status
                                                            }
                                                        </Text>
                                                        <br />
                                                        <Text style={{ fontSize: 13, color: "#bbb" }}>
                                                            {
                                                                selectedPengiriman === "01" 
                                                                    ? (typeof cfg.explain === "function" ? cfg.explain(item) : cfg.explain || item.status)
                                                                    : selectedPengiriman === "02" 
                                                                    ? (typeof cfg2.explain === "function" ? cfg2.explain(item) : cfg2.explain || item.status)
                                                                    : selectedPengiriman === "03" 
                                                                    ? (typeof cfg3.explain === "function" ? cfg3.explain(item) : cfg3.explain || item.status)
                                                                    : item.status
                                                            }
                                                        </Text>

                                                        <div style={{ marginTop: 8, color: "#999" }}>
                                                            <ClockCircleOutlined />
                                                            <Text style={{ marginLeft: 8, color: "#999" }}>
                                                                {date.format("DD MMM YYYY")}
                                                            </Text>
                                                            <Text
                                                                type="secondary"
                                                                style={{ marginLeft: 12, color: "#666" }}
                                                            >
                                                                {date.format("HH:mm")}
                                                            </Text>
                                                        </div>
                                                    </Card>
                                                </Timeline.Item>
                                            );
                                        })}
                                    </Timeline>
                                )}
                            </Spin>
                        </Card>
                    </Col>
                </Row>
            </div>

            {/* FOOTER */}
            <footer className="landing-footer">
                <div className="footer-container">
                    <div className="footer-main-grid">
                        <div className="footer-col footer-col-brand">
                            <p className="footer-brand-desc">{t.footerDesc}</p>
                        </div>

                        <div className="footer-col footer-col-links">
                            <h4 className="footer-col-title">{lang === "ar" ? "روابط سريعة" : lang === "id" ? "Navigasi Cepat" : "Quick Links"}</h4>
                            <ul className="footer-links-list">
                                <li><button onClick={() => navigate("/")}>{t.navHome}</button></li>
                                <li><button onClick={() => navigate("/about")}>{t.navAbout}</button></li>
                                <li><button onClick={() => navigate("/services")}>{t.navServices}</button></li>
                                <li><button onClick={() => navigate("/check-rates")}>{t.navCheckRates}</button></li>
                                <li><button onClick={() => navigate("/contact")}>{t.navContact}</button></li>
                            </ul>
                        </div>

                        <div className="footer-col footer-col-services">
                            <h4 className="footer-col-title">{lang === "ar" ? "وجهات الخدمة" : lang === "id" ? "Cakupan Destinasi" : "Service Coverage"}</h4>
                            <ul className="footer-links-list text-muted-list">
                                <li>🇾🇪 Yemen (Tarim, Mukalla, Aden, Sana'a)</li>
                                <li>🇸🇦 Saudi Arabia (Jeddah, Riyadh, Makkah)</li>
                                <li>📦 Air & Sea Cargo Shipping</li>
                                <li>💵 Outbound & Inbound Money Transfer</li>
                            </ul>
                        </div>
                    </div>

                    <div className="footer-bottom">
                        <p className="copyright-text">
                            &copy; {new Date().getFullYear()} INDAK. {t.footerCopy}
                        </p>
                        <div className="dev-tag">
                            <span className="dev-by">{lang === "ar" ? "تطوير بواسطة" : "Dev By"}</span>
                            <span className="dev-name">~ YallaBob ~</span>
                        </div>
                    </div>
                </div>
            </footer>
        </div>
    );
}